import { useState, useEffect, useCallback, useRef, Component } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { api } from './api';
import { InvoiceFilters } from './InvoiceFilters';
import { InvoiceListItem } from './InvoiceListItem';
import { InvoiceDetail } from './InvoiceDetail';
import { InvoiceGroup, groupItems } from './InvoiceGroup';
import { useInvoiceCleanup } from './InvoiceCleanup';
import { BulkBar } from './BulkBar';

class ErrorBoundary extends Component {
    constructor(p) { super(p); this.state = { crashed: false }; }
    static getDerivedStateFromError() { return { crashed: true }; }
    render() { return this.state.crashed ? this.props.fallback : this.props.children; }
}

const INIT = { statut: 'en_attente', mois: '', mode_mois: 'facture', scan_du: '', scan_au: '', fournisseur: '', immat: '', page: 1 };

const toParams = ({ mois, mode_mois, ...f }) => Object.fromEntries(Object.entries({ ...f, limit: 50, [mode_mois === 'scan' ? 'mois_scan' : 'mois_facture']: mois })
    .filter(([, x]) => x !== '' && x !== undefined && x !== null));

// Centre de traitement unique (Contrôle factures + onglet « Documents à traiter » de Frais véhicules)
export function InvoiceWorkspace({ month, onMonth, onData, heightClass = 'flex-1 min-h-0' }) {
    const [f, setF] = useState(() => ({ ...INIT, mois: month ?? '' }));
    const [data, setData] = useState({ items: [], total: 0, compteurs: {} });
    const [loading, setLoading] = useState(true);
    const [sel, setSel] = useState(null);
    const [err, setErr] = useState(null);
    const [rev, setRev] = useState(0);
    useEffect(() => { if (month !== undefined) setF(p => (p.mois === month ? p : { ...p, mois: month, page: 1 })); }, [month]);
    const setFilters = (fn) => { const n = typeof fn === 'function' ? fn(f) : fn; setF(n); if (onMonth && n.mois !== f.mois) onMonth(n.mois); };
    const reqId = useRef(0);
    const load = useCallback(async (keep) => {
        const my = ++reqId.current;
        setLoading(true);
        try {
            const r = await api.list(toParams(f));
            if (my !== reqId.current) return; // réponse d'un ancien filtre (changement de mois rapide) : ignorée
            setData(r);
            setErr(null);
            onData?.(r);
            if (!keep) setSel(s => (r.items.some(i => i.id === s) ? s : (window.innerWidth >= 1024 ? r.items[0]?.id : null)));
        } catch (e) {
            if (my === reqId.current) setErr('Liste momentanément indisponible — réessayez.');
        } finally { if (my === reqId.current) setLoading(false); }
    }, [f]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { const t = setTimeout(() => load(), 250); return () => clearTimeout(t); }, [load]);
    useEffect(() => { const t = setInterval(() => { if (document.visibilityState === 'visible') load(true); }, 60000); return () => clearInterval(t); }, [load]);

    const idx = data.items.findIndex(i => i.id === sel);
    const next = () => setSel(data.items[idx + 1]?.id || data.items[0]?.id || null);
    const done = (id) => {
        const nxt = data.items[idx + 1]?.id || data.items[idx - 1]?.id || null;
        if (['en_attente', 'a_valider'].includes(f.statut)) setData(p => ({ ...p, items: p.items.filter(i => i.id !== id), total: p.total - 1 }));
        setSel(nxt);
        setTimeout(() => load(true), 800);
    };
    const pages = Math.max(1, Math.ceil(data.total / 50));
    const [checked, setChecked] = useState(new Set());
    useEffect(() => { setChecked(new Set()); }, [f]);
    const toggle = (id, v) => setChecked(p => { const n = new Set(p); if (v) n.add(id); else n.delete(id); return n; });
    const cleanup = useInvoiceCleanup((ids) => {
        const gone = new Set(ids);
        setChecked(p => new Set([...p].filter(x => !gone.has(x))));
        if (f.statut !== 'toutes') setData(p => ({ ...p, items: p.items.filter(i => !gone.has(i.id)), total: p.total - ids.length }));
        if (gone.has(sel)) setSel(null);
        setTimeout(() => load(true), 500);
    });
    const tog = cleanup.canManage ? toggle : null;

    return (
        <div className={`${heightClass} flex gap-4`} data-testid="invoice-workspace">
            {cleanup.dialogs}
            <aside className={`${sel ? 'hidden lg:flex' : 'flex'} w-full lg:w-[340px] shrink-0 flex-col gap-3 min-h-0`}>
                <InvoiceFilters f={f} set={setFilters} counts={data.compteurs} />
                <div className="flex items-center gap-2">
                    {tog && data.items.length > 0 && <input type="checkbox" checked={data.items.every(i => checked.has(i.id))} onChange={e => data.items.forEach(i => toggle(i.id, e.target.checked))} className="w-4 h-4 accent-gray-900 cursor-pointer" aria-label="Tout sélectionner" data-testid="invoice-check-all" />}
                    <p className="text-[12px] text-gray-500" data-testid="invoice-result-count">{data.total} ligne(s){loading && <Loader2 className="inline w-3 h-3 ml-1 animate-spin" />}</p>
                </div>
                <BulkBar ids={[...checked]} statut={f.statut} c={cleanup} clear={() => setChecked(new Set())} reload={() => { load(true); setRev(n => n + 1); }} />
                {err && <p className="text-[12px] text-red-600" data-testid="invoice-list-error">{err} <button className="underline font-medium" onClick={() => load()} data-testid="invoice-list-retry">Réessayer</button></p>}
                <div className="flex-1 min-h-0 overflow-auto space-y-1 -mx-1 px-1" data-testid="invoice-list">
                    {groupItems(data.items).map(g => (g.items.length > 1
                        ? <InvoiceGroup key={g.key} g={g} sel={sel} setSel={setSel} checked={checked} toggle={tog} cleanup={cleanup} />
                        : <InvoiceListItem key={g.key} it={g.items[0]} active={g.items[0].id === sel} onClick={() => setSel(g.items[0].id)} checked={checked.has(g.items[0].id)} onCheck={tog ? v => toggle(g.items[0].id, v) : null} cleanup={cleanup} />))}
                    {!loading && !data.items.length && <p className="text-center text-[13px] text-gray-400 py-10" data-testid="invoice-list-empty">Aucune facture pour ces filtres</p>}
                </div>
                {pages > 1 && (
                    <div className="flex items-center justify-between text-[12px]" data-testid="invoice-pagination">
                        <Button variant="outline" size="sm" disabled={f.page <= 1} onClick={() => setF(p => ({ ...p, page: p.page - 1 }))}>Précédent</Button>
                        <span>Page {f.page} / {pages}</span>
                        <Button variant="outline" size="sm" disabled={f.page >= pages} onClick={() => setF(p => ({ ...p, page: p.page + 1 }))} data-testid="invoice-page-next">Suivant</Button>
                    </div>
                )}
            </aside>
            <section className={`${sel ? 'flex' : 'hidden lg:flex'} flex-1 min-w-0 min-h-0 flex-col`}>
                {sel ? <ErrorBoundary key={sel} fallback={<p className="p-6 text-sm text-red-600" data-testid="invoice-detail-crash">Impossible d'afficher cette facture. Sélectionnez-en une autre ou actualisez.</p>}>
                    <InvoiceDetail id={sel} rev={rev} cleanup={cleanup} onDone={done} onRefresh={() => load(true)} onNext={next} onBack={() => setSel(null)} onOpen={setSel} /></ErrorBoundary>
                    : <div className="h-full flex items-center justify-center text-sm text-gray-400">Sélectionnez une facture</div>}
            </section>
        </div>
    );
}
